import React, { MutableRefObject, useCallback } from 'react';
import { IconButton, Paper, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  DataGridPro,
  GridApiPro,
  GridCallbackDetails,
  GridCellParams,
  GridColDef,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import EditCalendar from '@mui/icons-material/EditCalendar';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { DateTime } from 'luxon';
import { TripLeg } from '../../../data/models/reservation.model';
import {
  TripLegStatus,
  tripStatusWithEtaList,
} from '../../../data/models/trip-leg-status.model';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as lookupSlice from '../../../data/lookup.slice';
import * as accountingSlice from '../../../data/accounting.slice';
import AssignProviderDialog from '../triplegs/AssignProviderDialog';
import ReopenTripLegDialog from '../triplegs/ReopenTripLegDialog';
import EditTripLegTimesDialog from '../triplegs/EditTripLegTimesDialog';
import dateUtils from '../../../services/DateUtils';

const PREFIX = 'TripLegList';

const classes = {
  container: `${PREFIX}-container`,
  grid: `${PREFIX}-grid`,
  chip: `${PREFIX}-chip`,
  loading: `${PREFIX}-loading`,
  wrapWhiteSpace: `${PREFIX}-wrapWhiteSpace`,
  tripDetail: `${PREFIX}-tripDetail`,
  subText: `${PREFIX}-subText`,
  actionContainer: `${PREFIX}-actionContainer`,
  dualValueCellContainer: `${PREFIX}-dualValueCellContainer`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  '.MuiDataGrid-columnHeaderTitle': {
    fontWeight: '700',
  },

  [`&.${classes.container}`]: {
    display: 'flex',
    justifyContent: 'stretch',
  },

  [`& .${classes.grid}`]: {
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
  },
  '.TripLegList-grid': {
    border: 'none !important',
  },

  '.MuiDataGrid-columnHeaders': {
    borderColor: '#b7adad !important',
  },

  [`& .${classes.chip}`]: {
    textTransform: 'uppercase',
  },

  [`& .${classes.grid} .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight)>.${classes.wrapWhiteSpace}`]:
    {
      whiteSpace: 'normal',
    },

  [`& .${classes.tripDetail}`]: {
    wordWrap: 'break-word',
    whiteSpace: 'normal',
    height: '100%',
    display: 'flex',
    flex: 1,
    alignItems: 'center',
  },

  [`& .${classes.subText}`]: {
    color: '#666',
  },

  [`& .${classes.dualValueCellContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
  },

  [`& .${classes.actionContainer}`]: {
    justifyContent: 'flex-end !important',
  },
}));

interface MonitorTodaysTripsTableProps {
  page: number;
  pageSize: number;
  pageSizeOptions: number[];
  rowCount: number;
  paginationChange: (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => void;
  sortModelChange: (
    model: GridSortModel,
    details: GridCallbackDetails<any>,
  ) => void;
  tripLegs: TripLeg[];
  loading: boolean;
  isSelectingTrips: boolean;
  showAssignAction?: boolean;
  showReopenAction?: boolean;
  showInProgress?: boolean;
  showEditTimesAction?: boolean;
  dataGridApiRef: MutableRefObject<GridApiPro>;
}

export default function MonitorTodaysTripsTable({
  page,
  pageSize,
  pageSizeOptions,
  rowCount,
  tripLegs,
  paginationChange,
  sortModelChange,
  loading,
  isSelectingTrips,
  showAssignAction = true,
  showReopenAction = true,
  showInProgress = false,
  showEditTimesAction = false,
  dataGridApiRef,
}: MonitorTodaysTripsTableProps) {
  const t = useCommonTranslation();

  const [dialogAssignOpen, setDialogAssignOpen] = React.useState(false);
  const [dialogReopenOpen, setDialogReopenOpen] = React.useState(false);
  const [dialogEditTimesOpen, setDialogEditTimesOpen] = React.useState(false);
  const [selectedTripLeg, setSelectedTripLeg] = React.useState<TripLeg>();
  const timezones = useAppSelector(lookupSlice.selectTimeZones);

  const formatEta = useCallback(
    (eta?: number): string => {
      if (!eta) {
        return '';
      }
      return `${Math.round(eta / 60)} ${t('common.minutes')}`;
    },
    [t],
  );

  const getPickupTime = useCallback(
    (params: GridCellParams) => {
      const tripLeg = params.row;
      const tripId = tripLeg.tripLegReferenceID;
      let timeString = '';

      if (tripLeg.isWillCall) {
        // This is the old code that conformed to the original spec. Apparently they want it to just show "Will Call" ticket F1V2-588
        // if (tripLeg.appointmentDate) {
        //   // Since the date has no time, we parse it and add 11 hours to ensure it displays the expected date.
        //   // If we didn't do this it would display the previous day since it parsed as a UTC date.
        //   const m = DateTime.fromISO(tripLeg.appointmentDate)
        //     .setZone(dateUtils.WORK_TIME_ZONE)
        //     .plus({ hours: 11 });
        //   timeString = `${m.toLocaleString(DateTime.TIME_SIMPLE)}, ${t('reservation.willCallTime')}`;
        // }
        timeString = t('reservation.willCallTime');
      } else {
        if (tripLeg.pickUpDueTimeUTC) {
          const tz = timezones.find(
            (t) => t.timeZoneID === tripLeg.pickUpAddressTimeZoneID,
          );
          const m = DateTime.fromISO(tripLeg.pickUpDueTimeUTC, {
            zone: tz?.ianaStandard,
          });
          timeString = m.toLocaleString(DateTime.TIME_SIMPLE);
        }
      }
      return (
        <div className={classes.dualValueCellContainer}>
          <span>{timeString || '-'}</span>
          <span className={classes.subText}>{tripId}</span>
        </div>
      );
    },
    [t, timezones],
  );

  const shouldShowEta = (params: GridCellParams): boolean => {
    const tripLegStatus = params.row.tripLegStatusBC?.toLowerCase();
    return tripStatusWithEtaList.includes(tripLegStatus as TripLegStatus);
  };

  const renderMember = (params: GridCellParams): React.ReactElement => {
    const passengerFirstName = params.row.passengerFirstName;
    const passengerLastName = params.row.passengerLastName;

    return (
      <div className={classes.dualValueCellContainer}>
        <span>{`${passengerFirstName} ${passengerLastName}`}</span>
        <span className={classes.subText}>
          {`${params.row.tripModeBC}${params.row.passengerCareLevelBC}`}
        </span>
      </div>
    );
  };

  const renderLastUpdate = (params: GridCellParams): React.ReactElement => {
    const tripLeg = params.row;
    const exceptionLog = tripLeg.tripLegExceptionLogs;

    if (!exceptionLog || exceptionLog.length === 0) {
      return <Typography className={classes.tripDetail}>{'-'}</Typography>;
    }

    const lastExceptionEntry = exceptionLog[exceptionLog.length - 1];
    const lastUpdateTime = DateTime.fromISO(lastExceptionEntry.createdOnUTC);

    if (!lastUpdateTime.isValid) {
      return <Typography className={classes.tripDetail}>{'-'}</Typography>;
    }

    const now = DateTime.now();
    const durationMillis = Math.abs(now.toMillis() - lastUpdateTime.toMillis());
    const totalMinutes = Math.floor(durationMillis / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const timeDisplay = `${hours}:${String(minutes).padStart(2, '0')}`;

    return (
      <Typography className={classes.tripDetail}>{timeDisplay}</Typography>
    );
  };

  const renderTimeOnList = (params: GridCellParams): React.ReactElement => {
    const tripLeg = params.row;
    const exceptionLog = tripLeg.tripLegExceptionLogs;

    if (!exceptionLog || exceptionLog.length === 0) {
      return <Typography className={classes.tripDetail}>{'-'}</Typography>;
    }

    const sortedLogs = [...exceptionLog].sort(
      (a, b) =>
        DateTime.fromISO(a.createdOnUTC).toMillis() -
        DateTime.fromISO(b.createdOnUTC).toMillis(),
    );

    let startIndex = -1;
    for (let i = sortedLogs.length - 1; i >= 0; i -= 1) {
      const entry = sortedLogs[i];
      if (
        entry.tripLegExceptionID === null ||
        entry.tripLegExceptionID === undefined
      ) {
        startIndex = i + 1;
        break;
      }
    }

    if (startIndex === -1) {
      startIndex = sortedLogs.findIndex(
        (e) =>
          e.tripLegExceptionID !== null && e.tripLegExceptionID !== undefined,
      );
    }

    if (startIndex === -1 || startIndex >= sortedLogs.length) {
      return <Typography className={classes.tripDetail}>{'-'}</Typography>;
    }

    const startEntry = sortedLogs[startIndex];
    const firstExceptionTime = DateTime.fromISO(startEntry.createdOnUTC);
    if (!firstExceptionTime.isValid) {
      return <Typography className={classes.tripDetail}>{'-'}</Typography>;
    }

    const now = DateTime.now();
    const durationMillis = Math.abs(
      now.toMillis() - firstExceptionTime.toMillis(),
    );
    const totalMinutes = Math.floor(durationMillis / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const timeDisplay = `${hours}:${String(minutes).padStart(2, '0')}`;

    return (
      <Typography className={classes.tripDetail}>{timeDisplay}</Typography>
    );
  };

  const renderException = (params: GridCellParams): React.ReactElement => {
    const getExceptionName = (exceptionCode: string): string => {
      if (!exceptionCode) return '';

      switch (exceptionCode.toUpperCase()) {
        case 'NODRIVER':
          return t('triplegInventory.noDriver');
        case 'DRIVERNOTONLINE':
          return t('triplegInventory.driverNotOnline');
        case 'DRIVERLATE':
          return t('triplegInventory.driverLate');
        default:
          return exceptionCode;
      }
    };

    const showEta = shouldShowEta(params);
    const driverLocations = params.row.tripLegDriverLocations;
    let etaDisplay = '?';

    if (driverLocations && driverLocations.length > 0) {
      const etaList = driverLocations
        .map((location: { estimatedTimeOfArrivalSeconds: number }) =>
          formatEta(location.estimatedTimeOfArrivalSeconds),
        )
        .filter(Boolean);
      etaDisplay = etaList.length > 0 ? etaList.join(', ') : '?';
    }

    return (
      <div className={classes.dualValueCellContainer}>
        <span className={classes.tripDetail}>
          {getExceptionName(params.row.tripLegExceptionBC || '')}
        </span>
        {showEta && (
          <span className={classes.subText}>
            {`${t('triplegInventory.eta')}: ${etaDisplay}`}
          </span>
        )}
      </div>
    );
  };

  const renderProviderName = (params: GridCellParams): React.ReactElement => {
    let provider = params.row.transportationProvider;
    let driver = params.row.driver;
    let providerName = '';

    if (provider && provider.transportationProviderName) {
      providerName = provider.transportationProviderName;
    }

    if (!providerName && params.row.transportationProviderName) {
      providerName = params.row.transportationProviderName;
    }

    let driverName = '';
    if (driver) {
      driverName = [
        driver.driverFirstName,
        driver.driverMiddleName,
        driver.driverLastName,
      ]
        .filter(Boolean)
        .join(' ');
    } else {
      const flatDriverName = [
        params.row.driverFirstName,
        params.row.driverMiddleName,
        params.row.driverLastName,
      ]
        .filter(Boolean)
        .join(' ');
      driverName = flatDriverName || params.row.manualDriverName || '';
    }

    return (
      <div className={classes.dualValueCellContainer}>
        <span className={classes.tripDetail}>{providerName}</span>
        <span className={classes.subText}>
          {driverName ? driverName : t('monitoring.noDriverAssigned')}
        </span>
      </div>
    );
  };

  const assignTripLeg = (tripLeg: TripLeg) => {
    setSelectedTripLeg(tripLeg);
    setDialogAssignOpen(true);
  };

  const reopenTripLeg = (tripLeg: TripLeg) => {
    setSelectedTripLeg(tripLeg);
    setDialogReopenOpen(true);
  };

  const canReopenTripLeg = (tripLeg: TripLeg) => {
    if (
      tripLeg.tripLegRejectedReasons &&
      tripLeg.tripLegStatusBC === TripLegStatus.Open.toLocaleUpperCase()
    ) {
      return true;
    }

    return false;
  };

  const editTripTimes = (tripLeg: TripLeg) => {
    setSelectedTripLeg(tripLeg);
    setDialogEditTimesOpen(true);
  };

  const closeAssignDialog = () => {
    setDialogAssignOpen(false);
  };

  const closeReopenDialog = () => {
    setDialogReopenOpen(false);
  };
  const closeEditTimesDialog = () => {
    setDialogEditTimesOpen(false);
  };

  const columns: GridColDef[] = [
    {
      field: 'pickUpDueTimeUTC',
      headerName: t('triplegInventory.pickup'),
      width: 150,
      sortable: true,
      renderCell: getPickupTime,
    },
    {
      field: 'member',
      headerName: t('triplegInventory.member'),
      flex: 1,
      sortable: false,
      renderCell: renderMember,
    },
    {
      field: 'transportationProviderName',
      headerName: t('triplegInventory.providerTitle'),
      flex: 1,
      renderCell: renderProviderName,
    },
    {
      field: 'lastUpdate',
      headerName: t('triplegInventory.lastUpdate'),
      flex: 1,
      sortable: false,
      align: 'center',
      renderCell: renderLastUpdate,
    },
    {
      field: 'timeOnList',
      headerName: t('triplegInventory.timeOnList'),
      flex: 1,
      renderCell: renderTimeOnList,
    },
    {
      field: 'exception',
      headerName: t('triplegInventory.exception'),
      flex: 1,
      renderCell: renderException,
    },
    {
      field: 'actions',
      headerName: ' ',
      width: 100,
      sortable: false,
      filterable: false,
      cellClassName: classes.actionContainer,
      renderCell: (params: GridCellParams) => (
        <>
          {showReopenAction && canReopenTripLeg(params.row) && (
            <Tooltip title={t('triplegInventory.reopen')}>
              <span>
                <IconButton
                  data-testid="reopen"
                  onClick={(_evt) => reopenTripLeg(params.row)}
                  size="medium"
                  sx={{
                    visibility: canReopenTripLeg(params.row)
                      ? 'visible'
                      : 'hidden',
                  }}
                  color="error">
                  <ReplayIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {showAssignAction && (
            <Tooltip title={t('triplegInventory.assign')}>
              <span>
                <IconButton
                  data-testid="edit"
                  disabled={params.row.tripLegStatusBC !== 'OPEN'}
                  onClick={(_evt) => assignTripLeg(params.row)}
                  size="medium"
                  color="primary">
                  <AssignmentTurnedInOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {showEditTimesAction &&
            params.row.tripLegStatusBC?.toLocaleLowerCase() ===
              TripLegStatus.Accepted && (
              <Tooltip title={t('triplegInventory.editEventTimes')}>
                <span>
                  <IconButton
                    data-testid="edit"
                    onClick={(_evt) => editTripTimes(params.row)}
                    size="medium"
                    color="primary">
                    <EditCalendar />
                  </IconButton>
                </span>
              </Tooltip>
            )}
        </>
      ),
    },
  ];

  return (
    <>
      <StyledPaper elevation={0} className={classes.container}>
        <DataGridPro
          getRowId={(row) => `${row.tripLegReferenceID}`}
          apiRef={dataGridApiRef}
          rows={tripLegs}
          rowCount={rowCount}
          columns={columns}
          pagination={true}
          paginationModel={{ page: page - 1, pageSize }}
          paginationMode="server"
          pageSizeOptions={pageSizeOptions}
          onPaginationModelChange={paginationChange}
          onSortModelChange={sortModelChange}
          autoHeight={true}
          loading={loading}
          className={classes.grid}
          checkboxSelection={isSelectingTrips}
          disableColumnSelector={true}
          hideFooter={false}
          sortingMode="server"
          sortingOrder={['asc', 'desc']}
        />
        {selectedTripLeg && dialogAssignOpen && (
          <AssignProviderDialog
            tripLeg={selectedTripLeg}
            open={dialogAssignOpen}
            close={closeAssignDialog}
          />
        )}
        {selectedTripLeg && dialogReopenOpen && (
          <ReopenTripLegDialog
            tripLeg={selectedTripLeg}
            open={dialogReopenOpen}
            close={closeReopenDialog}
          />
        )}
        {selectedTripLeg && dialogEditTimesOpen && (
          <EditTripLegTimesDialog
            tripLeg={selectedTripLeg}
            open={dialogEditTimesOpen}
            close={closeEditTimesDialog}
          />
        )}
      </StyledPaper>
    </>
  );
}
